import { type ReactNode, useEffect } from 'react';

import {
  BaseButton,
  BaseEmptyState,
  BaseLoadingState,
  BaseSystemState,
} from '../base';
import type { CapabilityTranslate } from './contracts';
import { DESIGN_CAPABILITY_I18N_KEYS } from './i18n';
import { toast } from '../toast';

export type CapabilityStatusKind =
  | 'loading'
  | 'empty'
  | 'error'
  | 'forbidden';

export interface CapabilityStatusProps {
  kind: CapabilityStatusKind;
  translate: CapabilityTranslate;
  code?: string;
  titleKey?: string;
  descriptionKey?: string;
  actions?: ReactNode;
  onRetry?: () => void;
  presentation?: 'legacy-data-explorer';
  loadingLabelKey?: string;
  emptyLabelKey?: string;
  errorLabelKey?: string;
}

const STATUS_KEYS = DESIGN_CAPABILITY_I18N_KEYS.status;

export const CapabilityStatus = ({
  kind,
  translate,
  code,
  titleKey,
  descriptionKey,
  actions,
  onRetry,
  presentation,
  loadingLabelKey,
  emptyLabelKey,
  errorLabelKey,
}: CapabilityStatusProps) => {
  const keys = STATUS_KEYS[kind];
  const values = code ? { code } : undefined;
  const title = translate(titleKey ?? keys.title, values);
  const description = translate(descriptionKey ?? keys.description, values);
  const resolvedActions = actions ?? (
    kind === 'error' && onRetry ? (
      <BaseButton
        label={translate(STATUS_KEYS.retry)}
        onClick={onRetry}
      />
    ) : undefined
  );
  const legacyErrorToastMessage =
    presentation === 'legacy-data-explorer' && kind === 'error' && errorLabelKey
      ? translate(errorLabelKey)
      : undefined;

  useEffect(() => {
    if (legacyErrorToastMessage) toast.error(legacyErrorToastMessage);
  }, [legacyErrorToastMessage]);

  if (presentation === 'legacy-data-explorer') {
    return (
      <div
        className="col-span-full flex h-full min-h-0 flex-1 flex-col overflow-hidden"
        data-presentation="legacy-data-explorer-status"
      >
        {kind === 'loading' ? (
          <BaseLoadingState variant="skeleton" className="min-h-64" title={loadingLabelKey ? translate(loadingLabelKey) : title} />
        ) : (
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">
              {translate(
                emptyLabelKey ?? DESIGN_CAPABILITY_I18N_KEYS.status.empty.title,
              )}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (kind === 'loading') {
    return <BaseLoadingState variant="skeleton" title={title} />;
  }
  if (kind === 'empty') {
    return (
      <BaseEmptyState
        title={emptyLabelKey ? translate(emptyLabelKey) : title}
        description={emptyLabelKey ? undefined : description}
        actions={resolvedActions}
      />
    );
  }
  return (
    <BaseSystemState
      kind={kind === 'forbidden' ? 'permission' : 'error'}
      title={title}
      description={description}
      actions={resolvedActions}
    />
  );
};
